import { type ReactNode, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

interface PageSectionProps {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  badge?: number | string;
  className?: string;
  'data-testid'?: string;
  /** Heading level for the section title, so callers can preserve a correct h1→h2→h3
   *  hierarchy on the page they render into. Defaults to `3` (today's behavior) for every
   *  caller that doesn't pass it. */
  headingLevel?: 2 | 3;
}

export function PageSection({
  title,
  action,
  children,
  collapsible = false,
  defaultCollapsed = false,
  badge,
  className,
  'data-testid': testId,
  headingLevel = 3,
}: PageSectionProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const HeadingTag = headingLevel === 2 ? 'h2' : 'h3';

  const headerContent = title && (
    <div
      className={cn(
        'flex items-center justify-between',
        collapsible && 'cursor-pointer select-none',
      )}
      onClick={collapsible ? () => setCollapsed((c) => !c) : undefined}
      role={collapsible ? 'button' : undefined}
      aria-expanded={collapsible ? !collapsed : undefined}
      tabIndex={collapsible ? 0 : undefined}
      onKeyDown={
        collapsible
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setCollapsed((c) => !c);
              }
            }
          : undefined
      }
    >
      <div className="flex items-center gap-2">
        <HeadingTag className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </HeadingTag>
        {badge != null && (
          <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 min-w-5 justify-center">
            {badge}
          </Badge>
        )}
        {collapsible && (
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              collapsed && '-rotate-90',
            )}
          />
        )}
      </div>
      {action && !collapsed && (
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          {action}
        </div>
      )}
    </div>
  );

  return (
    <section
      data-testid={testId}
      className={cn(
        // Mobile: full-bleed with top border
        'border-t border-border py-4 px-[var(--page-padding-x)]',
        // Desktop: card style
        'md:border md:rounded-lg md:p-[var(--card-padding)]',
        className,
      )}
    >
      {headerContent}
      {!collapsed && (
        <div className={cn(title && 'mt-3')}>
          {children}
        </div>
      )}
    </section>
  );
}
